import * as React from 'react';
import { AppContext } from '../../../App';
import { DialogContentText, Tooltip, IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import BlockIcon from '@mui/icons-material/Block';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { usePackageUpdate } from '../../hooks/packages/usePackageUpdate';
import { PackageStatuses } from '../../../types/packager-types';
import { AxiosError } from 'axios';

export interface IPackageFreezeProps {
  packageId: string;
  packageStatus: PackageStatuses;
}

const PackageFreeze: React.FC<IPackageFreezeProps> = ({ packageId, packageStatus }) => {
  const [open, setOpen] = React.useState(false);

  const { setToast } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  const theme = useTheme();

  const mutationPackageUpdate = usePackageUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['packager']
      });
      queryClient.invalidateQueries({
        queryKey: ['packages']
      });
      // The detail query gates entry edits in PackagerDocuments.
      queryClient.invalidateQueries({ queryKey: ['package'] });
      setToast({
        variant: 'success',
        open: true,
        message: 'Package frozen successfully!',
      });
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Failed to freeze package: ${error.message}`,
      });
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  const handleUpdate = () => {
    mutationPackageUpdate.mutate({
      packageId: packageId!,
      data: {
        status: PackageStatuses.FROZEN,
      },
    });
    setOpen(false);
  };

  if (packageStatus === PackageStatuses.FROZEN) {
    return null;
  }

  return (
    <>
      <Tooltip placement="top" title={'Freeze Package'} disableFocusListener disableTouchListener>
        <IconButton
          data-cy={`btn-freeze-package-${packageId}`}
          onClick={!mutationPackageUpdate.isPending ? handleClickOpen : undefined}
          disabled={mutationPackageUpdate.isPending}
          sx={{
            color: theme.palette.custom.lightGreen,
            '&:hover': {
              backgroundColor: theme.palette.custom.lightGreen + '20', // 20% opacity for hover
            },
          }}
        >
          <BlockIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <DialogGlynt
        open={open}
        closeModal={handleCloseConfirmation}
        loading={mutationPackageUpdate.isPending}
        cypressId="dialog-package-freeze"
      >
        <DialogTitleGlynt
          title="Confirmation"
          closeModal={handleCloseConfirmation}
          loading={mutationPackageUpdate.isPending}
        />
        <DialogContentGlynt>
          <DialogContentText>Are you sure you want to freeze this package?</DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleUpdate}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-package-freeze-modal-cancel"
          submitCypressId="btn-package-freeze-modal-yes"
          clearCypressId=""
          submitButtonText="Yes"
          submitLoading={mutationPackageUpdate.isPending}
          showClearButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default PackageFreeze;
